import React, { useEffect, useRef, useState, useMemo } from "react";
import { Play, Pause, SkipBack, SkipForward } from "lucide-react";
import heroImage from "../assets/hero_image_desktop.png";
import heroImageMobile from "../assets/hero_image.png";
import brandLogo from "../assets/brand_logo.png";
import { songList } from "../utils/songList";
import { useNavigate } from "react-router-dom";

const InitialLoader = ({ progress, currentTask }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1e0e0a]/95 backdrop-blur-sm">
    <div className="relative flex flex-col items-center gap-6">
      <img className="w-44" src={brandLogo} alt="Brand Logo" />

      <div className="w-64 md:w-80">
        <div className="h-1.5 bg-[#b58249]/30 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#e9c17b] to-[#d4a853] rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="text-center">
        <p className="text-[#e4cfaa]/60 text-xs mt-1">
          Loading some awesome sound tracks for you...
        </p>
      </div>
    </div>
  </div>
);

function HomePage() {
  const navigate = useNavigate();
  const [currentlyPlayingIndex, setCurrentlyPlayingIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [selectedLanguage, setSelectedLanguage] = useState("English");
  const [isLoading, setIsLoading] = useState(true);
  const [loadProgress, setLoadProgress] = useState(0);
  const [currentTask, setCurrentTask] = useState("Loading languages...");

  const audioRef = useRef(new Audio());
  const isSeekingRef = useRef(false);
  const loadingIntervalRef = useRef(null);

  useEffect(() => {
    const startTime = Date.now();
    const duration = 3000;

    loadingIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const nextProgress = Math.min((elapsed / duration) * 100, 100);
      setLoadProgress(nextProgress);

      if (nextProgress >= 100) {
        clearInterval(loadingIntervalRef.current);
        setIsLoading(false);
      }
    }, 16);

    const timeoutId = setTimeout(() => {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
        setIsLoading(false);
      }
    }, 3500);

    return () => {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
      }
      clearTimeout(timeoutId);
    };
  }, []);

  const languages = useMemo(() => {
    const uniqueLanguages = [...new Set(songList.map((song) => song.language))];
    const customOrder = ["English", "Hindi"];

    const orderedLanguages = [];
    const otherLanguages = [];

    uniqueLanguages.forEach((lang) => {
      if (customOrder.includes(lang)) {
        orderedLanguages.push(lang);
      } else {
        otherLanguages.push(lang);
      }
    });

    orderedLanguages.sort(
      (a, b) => customOrder.indexOf(a) - customOrder.indexOf(b),
    );
    otherLanguages.sort();

    return [...new Set([...orderedLanguages, ...otherLanguages])];
  }, []);

  const filteredSongs = useMemo(() => {
    return songList.filter((song) => song.language === selectedLanguage);
  }, [selectedLanguage]);

  // useEffect(() => {
  //   const threshold = 160;

  //   const isMobile = () =>
  //     /Android|iPhone|iPad|iPod|Opera Mini|IEMobile/i.test(
  //       navigator.userAgent,
  //     ) || window.innerWidth < 768;

  //   const redirectAway = () => {
  //     const audio = audioRef.current;
  //     audio.pause();
  //     audio.currentTime = 0;
  //     setIsPlaying(false);
  //     setCurrentlyPlayingIndex(null);
  //     navigate("/warning", { replace: true });
  //   };

  //   const sizeGapOpen = () =>
  //     window.outerWidth - window.innerWidth > threshold ||
  //     window.outerHeight - window.innerHeight > threshold;

  //   const timingOpen = () => {
  //     const start = performance.now();
  //     debugger; // eslint-disable-line no-debugger
  //     const end = performance.now();
  //     return end - start > 50;
  //   };

  //   const detectDevTools = () => {
  //     if (isMobile()) return;
  //     if (sizeGapOpen() || timingOpen()) redirectAway();
  //   };

  //   detectDevTools();
  //   const t = setTimeout(detectDevTools, 300);

  //   window.addEventListener("resize", detectDevTools);
  //   const interval = setInterval(detectDevTools, 800);

  //   return () => {
  //     clearTimeout(t);
  //     clearInterval(interval);
  //     window.removeEventListener("resize", detectDevTools);
  //   };
  // }, [navigate]);

  const stopAudio = () => {
    const audio = audioRef.current;
    audio.pause();
    audio.currentTime = 0;

    setIsPlaying(false);
    setCurrentlyPlayingIndex(null);
    setProgress(0);
    setCurrentTime(0);
  };

  useEffect(() => {
    if (currentlyPlayingIndex !== null) {
      const currentSong = songList[currentlyPlayingIndex];
      if (currentSong?.language !== selectedLanguage) {
        stopAudio();
      }
    }
  }, [selectedLanguage, currentlyPlayingIndex]);

  useEffect(() => {
    const audio = audioRef.current;

    const updateProgress = () => {
      if (isSeekingRef.current) return;

      const dur = audio.duration || 0;
      const ct = audio.currentTime || 0;

      setDuration(dur);
      setCurrentTime(ct);
      setProgress(dur ? (ct / dur) * 100 : 0);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentlyPlayingIndex(null);
      setProgress(0);
    };

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("ended", handleEnded);
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  const playSong = async (filteredIndex) => {
    const audio = audioRef.current;

    const actualIndex = songList.findIndex(
      (song) => song.id === filteredSongs[filteredIndex].id,
    );

    try {
      if (currentlyPlayingIndex === actualIndex) {
        if (isPlaying) {
          audio.pause();
          setIsPlaying(false);
        } else {
          await audio.play();
          setIsPlaying(true);
        }
        return;
      }

      audio.pause();
      audio.currentTime = 0;
      audio.src = filteredSongs[filteredIndex].url;
      await audio.play();

      setCurrentlyPlayingIndex(actualIndex);
      setIsPlaying(true);
    } catch (err) {
      console.warn("Playback error:", err);
    }
  };

  const goNext = () => {
    if (currentlyPlayingIndex === null) return;

    const currentFilteredIndex = filteredSongs.findIndex(
      (song) => song.id === songList[currentlyPlayingIndex].id,
    );

    if (currentFilteredIndex === -1) return;

    const nextFilteredIndex =
      currentFilteredIndex === filteredSongs.length - 1
        ? 0
        : currentFilteredIndex + 1;

    const nextActualIndex = songList.findIndex(
      (song) => song.id === filteredSongs[nextFilteredIndex].id,
    );

    playSongByActualIndex(nextActualIndex);
  };

  const goPrev = () => {
    if (currentlyPlayingIndex === null) return;

    const currentFilteredIndex = filteredSongs.findIndex(
      (song) => song.id === songList[currentlyPlayingIndex].id,
    );

    if (currentFilteredIndex === -1) return;

    const prevFilteredIndex =
      currentFilteredIndex === 0
        ? filteredSongs.length - 1
        : currentFilteredIndex - 1;

    const prevActualIndex = songList.findIndex(
      (song) => song.id === filteredSongs[prevFilteredIndex].id,
    );

    playSongByActualIndex(prevActualIndex);
  };

  const playSongByActualIndex = (actualIndex) => {
    const audio = audioRef.current;

    audio.pause();
    audio.currentTime = 0;
    audio.src = songList[actualIndex].url;
    audio.play();

    setCurrentlyPlayingIndex(actualIndex);
    setIsPlaying(true);
  };

  const formatTime = (s) => {
    if (!s) return "0:00";
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  const handleSeek = (e) => {
    const val = Number(e.target.value);
    const audio = audioRef.current;

    const newTime = (val / 100) * audio.duration;
    audio.currentTime = newTime;

    setProgress(val);
  };

  const currentSong = useMemo(() => {
    return currentlyPlayingIndex !== null
      ? songList[currentlyPlayingIndex]
      : null;
  }, [currentlyPlayingIndex]);

  if (isLoading) {
    return <InitialLoader progress={loadProgress} currentTask={currentTask} />;
  }

  return (
    <div className="min-h-screen p-2 md:p-5 relative overflow-x-hidden">
      {/* Ornaments */}
      <div className="fixed top-3 left-3 w-28 h-28 border border-[#d3a85f]/20 rounded-2xl pointer-events-none opacity-20">
        <div className="absolute top-2 left-2 w-8 h-8 border-2 border-[#e2b768]/35 rounded-full" />
        <div className="absolute top-4 left-4 w-4 h-4 border-2 border-[#e2b768]/35 rounded-full" />
      </div>

      <div className="fixed top-3 right-3 w-28 h-28 border border-[#d3a85f]/20 rounded-2xl pointer-events-none opacity-20">
        <div className="absolute top-2 right-2 w-8 h-8 border-2 border-[#e2b768]/35 rounded-full" />
        <div className="absolute top-4 right-4 w-4 h-4 border-2 border-[#e2b768]/35 rounded-full" />
      </div>

      <main className="max-w-6xl mx-auto relative z-10 border-2 border-[#a06c31]/75 rounded-xl overflow-hidden shadow-2xl bg-gradient-to-b from-[#422216]/95 to-[#160a07]/98">
        <div className="relative m-1.5 max-md:m-0 border border-[#dfb874]/40 rounded-2xl pointer-events-none" />

        {/* HERO */}
        <section className="relative h-[450px] md:h-[480px] lg:h-[560px] overflow-hidden border-b border-[#e8c07e]/30">
          <img
            src={heroImage}
            className="w-full h-full object-fill hidden md:block"
            alt="poster"
          />

          <img
            src={heroImageMobile}
            className="w-full h-full block md:hidden"
            alt="poster"
          />

          <div className="absolute inset-0 bg-gradient-to-t from-[#110806]/20 via-[#110806]/10 to-transparent" />
        </section>

        {/* PLAYLIST */}
        <section className="px-4 md:px-6 pt-4 max-md:pt-2 bg-gradient-to-b from-[#462215]/90 to-[#1e0e0a]/95">
          {/* Language Tabs */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-6 pb-2 border-b border-[#b58249]/40">
            {languages.map((language) => (
              <button
                key={language}
                onClick={() => setSelectedLanguage(language)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${selectedLanguage === language
                    ? "bg-gradient-to-r from-[#e9c17b] to-[#d4a853] text-[#1e0e0a] shadow-md"
                    : "bg-[#2a160e]/60 text-[#e4cfaa] border border-[#b58249]/40 hover:bg-[#3d2015]/80 hover:border-[#e9c17b]/60"
                  }`}
              >
                {language}
              </button>
            ))}
          </div>

          {/* Song list */}
          <div className="h-[23vh] max-md:h-[28vh] overflow-auto custom-scrollbar">
            <div
              className={`grid gap-2 pb-10 ${currentSong ? "max-md:pb-32" : "max-md:pb-10"
                } pr-2`}
            >
              {filteredSongs.length === 0 ? (
                <div className="text-center text-[#e4cfaa] py-8">
                  No songs found in this language
                </div>
              ) : (
                filteredSongs.map((song, filteredIndex) => {
                  const actualIndex = songList.findIndex(
                    (s) => s.id === song.id,
                  );
                  const active = actualIndex === currentlyPlayingIndex;

                  return (
                    <button
                      key={`${song.id}-${filteredIndex}`}
                      className={`glass-song-card group relative overflow-hidden w-full grid grid-cols-[36px_60px_minmax(0,1fr)_80px] md:grid-cols-[42px_70px_minmax(0,1fr)_140px_80px] items-center gap-2 md:gap-4 py-2.5 px-2 md:px-4 rounded-xl text-left cursor-pointer transition-all duration-300 ${active
                          ? "bg-gradient-to-b from-[#69381f]/65 to-[#34190f]/70 border-[#e9c17b]/70 shadow-lg"
                          : "bg-gradient-to-b from-[#5a301b]/45 to-[#2f170f]/55 border-[#b58249]/40 hover:border-[#e9c17b]/50 hover:from-[#69381f]/55 hover:to-[#34190f]/65 hover:-translate-y-px"
                        } border shadow-md backdrop-blur-xl`}
                      onClick={() => playSong(filteredIndex)}
                    >
                      <span className="glass-song-card__glow" />
                      <span className="glass-song-card__shine" />
                      <span className="glass-song-card__grain" />

                      <span className="relative z-10 text-[#f2deb0] text-center text-base md:text-lg opacity-90">
                        {filteredIndex + 1}
                      </span>

                      <span className="relative z-10 w-10 h-10 md:w-14 md:h-14 rounded-full grid place-items-center text-[#fff8e3] border-2 border-[#e6bd75]/80 bg-gradient-to-b from-[#301c15]/95 to-[#0d0807]/95 shadow-md">
                        {active && isPlaying ? (
                          <Pause size={18} className="md:w-5 md:h-5" />
                        ) : (
                          <Play size={18} className="md:w-5 md:h-5 ml-0.5" />
                        )}
                      </span>

                      <div className="relative z-10 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-[#f8ecd0] text-sm md:text-base whitespace-nowrap overflow-hidden text-ellipsis">
                            {song.title}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </section>
      </main>

      {/* PLAYER BAR */}
      {currentSong && (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center">
          <div className="w-full max-w-6xl mx-auto px-3 md:p-5">
            <footer className="grid grid-cols-1 md:grid-cols-[1.2fr_auto_1fr_auto] items-center gap-4 py-0 pb-2 px-4 md:py-4 md:px-6 bg-gradient-to-b from-[#31170e]/98 to-[#120907]/99 border-t border-[#d6a65c]/40 shadow-xl backdrop-blur-sm rounded-t-2xl">
              <div className="absolute inset-x-0 bottom-0 h-full bg-radial from-[#ebc172]/20 via-transparent to-transparent pointer-events-none rounded-t-2xl" />

              <div className="relative z-0 text-center md:text-left">
                <div className="text-[#fff1cb] font-semibold text-base md:text-lg max-md:hidden">
                  {currentSong.title}
                </div>
                <div className="text-[#e4cfaa] text-sm">
                  {currentSong.artist}
                </div>
              </div>

              <div className="relative z-0 flex items-center justify-center gap-3 md:gap-4">
                <button
                  className="text-[#fff6da] transition-transform hover:scale-105 w-5 h-5"
                  onClick={goPrev}
                >
                  <SkipBack size={22} className="md:w-4 md:h-4" />
                </button>

                <button
                  className="w-10 h-10 md:w-15 md:h-15 rounded-full grid place-items-center border-2 border-[#e5bc6e]/80 bg-gradient-to-b from-[#42291d]/95 to-[#170c09]/98 shadow-md transition-transform hover:scale-105"
                  onClick={() =>
                    playSong(
                      filteredSongs.findIndex((s) => s.id === currentSong.id),
                    )
                  }
                >
                  {isPlaying ? (
                    <Pause
                      size={26}
                      className="md:w-4 max-md:w-3.5 md:h-4 max-md:h-3.5"
                    />
                  ) : (
                    <Play
                      size={26}
                      className="md:w-4 max-md:w-3.5 md:h-4 max-md:h-3.5 ml-0.5"
                    />
                  )}
                </button>

                <button
                  className="text-[#fff6da] transition-transform hover:scale-105"
                  onClick={goNext}
                >
                  <SkipForward size={22} className="md:w-4 md:h-4" />
                </button>
              </div>

              <div className="relative z-0 flex items-center justify-center gap-3 flex-wrap md:justify-start">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={progress}
                  onChange={handleSeek}
                  className="w-[180px] md:w-[260px] h-1.5 rounded-full appearance-none bg-gradient-to-r from-[#5fcde8] to-[#8ecfdd] bg-[length:var(--value)_100%] bg-no-repeat [background-size:var(--value)_100%]"
                  style={{ "--value": `${progress}%` }}
                />
                <span className="text-[#f2deae] text-xs font-mono min-w-[70px]">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>
            </footer>
          </div>
        </div>
      )}

      <style jsx>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #e9d49f;
          border: 2px solid rgba(74, 48, 14, 0.55);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
          cursor: pointer;
        }

        input[type="range"]::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #e9d49f;
          border: 2px solid rgba(74, 48, 14, 0.55);
          cursor: pointer;
        }

        input[type="range"] {
          -webkit-appearance: none;
          appearance: none;
        }

        .bg-radial {
          background: radial-gradient(
            circle at 50% 0%,
            rgba(235, 193, 114, 0.18),
            transparent 48%
          );
        }

        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }

        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(90, 48, 27, 0.4);
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e9c17b;
          border-radius: 10px;
        }

        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #f0d08e;
        }

        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #e9c17b rgba(90, 48, 27, 0.4);
        }

        /* Glass animated card */
        .glass-song-card {
          position: relative;
          isolation: isolate;
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.08),
            inset 0 -1px 0 rgba(255, 255, 255, 0.03),
            0 8px 24px rgba(0, 0, 0, 0.2);
        }

        .glass-song-card::before {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background: linear-gradient(
            135deg,
            rgba(255, 255, 255, 0.08) 0%,
            rgba(255, 255, 255, 0.02) 35%,
            rgba(255, 255, 255, 0.01) 100%
          );
          pointer-events: none;
          z-index: 0;
        }

        .glass-song-card__glow {
          position: absolute;
          inset: -40%;
          border-radius: 999px;
          background: radial-gradient(
            circle,
            rgba(233, 193, 123, 0.18) 0%,
            rgba(233, 193, 123, 0.06) 28%,
            transparent 65%
          );
          filter: blur(18px);
          animation: glassFloat 7s ease-in-out infinite;
          pointer-events: none;
          z-index: 0;
        }

        .glass-song-card__shine {
          position: absolute;
          top: -120%;
          left: -40%;
          width: 50%;
          height: 320%;
          transform: rotate(22deg);
          background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 0.13) 45%,
            rgba(255, 255, 255, 0) 100%
          );
          animation: glassShine 5.8s linear infinite;
          pointer-events: none;
          z-index: 0;
        }

        .glass-song-card__grain {
          position: absolute;
          inset: 0;
          border-radius: inherit;
          background-image: radial-gradient(
            rgba(255, 255, 255, 0.06) 0.6px,
            transparent 0.6px
          );
          background-size: 8px 8px;
          opacity: 0.12;
          mix-blend-mode: soft-light;
          pointer-events: none;
          z-index: 0;
        }

        .glass-song-card:hover .glass-song-card__glow {
          animation-duration: 4.5s;
        }

        .glass-song-card:hover .glass-song-card__shine {
          animation-duration: 3.8s;
        }

        @keyframes glassFloat {
          0% {
            transform: translate3d(-10%, -6%, 0) scale(1);
          }
          50% {
            transform: translate3d(12%, 8%, 0) scale(1.08);
          }
          100% {
            transform: translate3d(-10%, -6%, 0) scale(1);
          }
        }

        @keyframes glassShine {
          0% {
            transform: translateX(-180%) rotate(22deg);
            opacity: 0;
          }
          12% {
            opacity: 1;
          }
          50% {
            opacity: 0.85;
          }
          100% {
            transform: translateX(340%) rotate(22deg);
            opacity: 0;
          }
        }

        @media (max-width: 767px) {
          .glass-song-card {
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
          }

          .glass-song-card__glow {
            opacity: 0.9;
            filter: blur(16px);
          }

          .glass-song-card__shine {
            opacity: 0.75;
          }
        }
      `}</style>
    </div>
  );
}

export default HomePage;
